import React, { useRef } from "react";
import {
  editBusinessMatchingOwnedBusinessEntityPath,
  newBusinessMatchingOwnedBusinessEntityPath,
  rootPath,
} from "../../../../../routes";
import Button from "../../../shared/components/ui/Button";
import { Box } from "../../../shared/components/ui/chakra";
import AddIcon from "../../../shared/components/icons/AddIcon";
import ArrowRightAltIcon from "../../../shared/components/icons/ArrowRightAltIcon";
import DehazeIcon from "../../../shared/components/icons/DehazeIcon";
import LightbulbIcon from "../../../shared/components/icons/LightbulbIcon";
import SettingsIcon from "../../../shared/components/icons/SettingsIcon";
import {
  SharedCurrentUser,
  SharedOwnedBusinessEntity,
} from "../../../shared/lib/types";
import { useSetCurrentBusinessEntityCode } from "../lib/useSetCurrentBusinessEntityCode";
import BusinessEntityStatusTag from "./BusinessEntityStatusTag";
import { addBusinessEntityButtonLabel } from "../lib/addBusinessEntityButtonLabel";
import { filterNotCurrentOwnedBusinessEntities } from "../lib/filterNotCurrentOwnedBusinessEntities";


import {
  Avatar,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";

const BusinessEntityListItem = ({
  businessEntity,
}: {
  businessEntity: SharedOwnedBusinessEntity;
}) => {
  const setCurrentBusinessEntityCode = useSetCurrentBusinessEntityCode();

  return (
    <Stack
      gap={1}
      px={5}
      pt={4}
      _hover={{ backgroundColor: "brand.50" }}
      role="button"
      onClick={() => setCurrentBusinessEntityCode(businessEntity.code)}
    >
      <Text fontSize={"sm"}>{businessEntity.name}</Text>
      <BusinessEntityStatusTag businessEntity={businessEntity} />
      <Box
        w={"full"}
        borderColor={"border.cardOutline"}
        borderBottomWidth={1}
        mt={3}
      />
    </Stack>
  );
};

const BusinessMatchingHeaderDrawer = ({
  currentUser,
}: {
  currentUser: SharedCurrentUser;
}) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Stack as="button" gap={0} align="center" ref={btnRef} onClick={onOpen}>
        <Flex
          boxSize="28px"
          align="center"
          justify="center"
          position="relative"
        >
          <DehazeIcon />
        </Flex>
        <Box fontSize="xs">メニュー</Box>
      </Stack>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
        size="xs"
      >
        <DrawerOverlay />
        <DrawerContent maxW={{ base: "calc(100% - 40px)", md: "lg" }}>
          <DrawerCloseButton />
          <DrawerHeader p={5}>
            <Text fontWeight={"bold"} fontSize={"md"}>
              受発注メニュー
            </Text>
          </DrawerHeader>
          <DrawerBody p={0}>
            <Stack bgColor={"brand.100"} p={5} gap={3}>
              <HStack gap={1} ml={-1}>
                <LightbulbIcon color={"brand.500"} />
                <Text
                  fontSize={"sm"}
                  fontWeight={"bold"}
                  color={"brand.500"}
                  pt={0.5}
                >
                  現在は、受発注専用の表示です。
                </Text>
              </HStack>
              <Box fontSize="xs" color={"textGray"}>
                個人アカウントの設定や、受発注以外のサービスを利用したい場合は、Q&A・勉強会の表示に戻ってください。
              </Box>
              <Button
                w="full"
                size="sm"
                leftIcon={<ArrowRightAltIcon />}
                iconSpacing={1}
                as="a"
                href={rootPath()}
              >
                Q&A・勉強会に戻る
              </Button>
            </Stack>
            {currentUser?.has_any_business_entities && (
              <>
                <Stack
                  p={5}
                  gap={3}
                  borderBottomWidth={1}
                  borderColor={"border.cardOutline"}
                >
                  <HStack gap={2} flexWrap="wrap">
                    <Avatar src={""} boxSize={7} />
                    <Text fontWeight="bold" pt={0.5}>
                      {currentUser.owned_business_entities.current.name}
                    </Text>
                    <BusinessEntityStatusTag
                      businessEntity={
                        currentUser.owned_business_entities.current
                      }
                    />
                  </HStack>
                  <Button
                    size={"sm"}
                    variant="outline"
                    leftIcon={<SettingsIcon boxSize={"18px"} />}
                    as="a"
                    target="_blank"
                    rel="noreferrer"
                    href={editBusinessMatchingOwnedBusinessEntityPath(
                      currentUser.owned_business_entities.current.code,
                    )}
                  >
                    事業者設定
                  </Button>
                </Stack>
                {filterNotCurrentOwnedBusinessEntities(
                  currentUser.owned_business_entities,
                ).length > 0 && (
                    <>
                      <Text
                        fontWeight={"bold"}
                        fontSize={"sm"}
                        px={5}
                        pt={5}
                        pb={2}
                      >
                        そのほか利用中の事業者一覧
                      </Text>
                      {filterNotCurrentOwnedBusinessEntities(
                        currentUser.owned_business_entities,
                      ).map((businessEntity) => (
                        <BusinessEntityListItem
                          businessEntity={businessEntity}
                          key={businessEntity.code}
                        />
                      ))}
                    </>
                  )}
                <Stack p={5}>
                  <Button
                    size={"xs"}
                    variant="outline"
                    leftIcon={<AddIcon boxSize={4} />}
                    as="a"
                    href={newBusinessMatchingOwnedBusinessEntityPath()}
                  >
                    {addBusinessEntityButtonLabel(currentUser)}
                  </Button>
                </Stack>
              </>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default BusinessMatchingHeaderDrawer;
